import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import DictationInput from '../../components/DictationInput';
import { MessageSquare } from 'lucide-react';
import { timeAgo } from '../../lib/utils';
import { toast } from 'sonner';

export default function HerMessagesPage() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const [contacts, setContacts] = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get('/her/messages/contacts').then(r => {
      setContacts(r.data);
      if (r.data.length > 0) setActiveContact(r.data[0]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!activeContact) return;
    api.get(`/her/messages?contact_id=${activeContact.id}`).then(r => setMessages(r.data)).catch(() => {});
  }, [activeContact]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!newMsg.trim() || !activeContact) return;
    try {
      await api.post('/her/messages', { receiver_id: activeContact.id, content: newMsg.trim() });
      setNewMsg('');
      const r = await api.get(`/her/messages?contact_id=${activeContact.id}`);
      setMessages(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to send message');
    }
  };

  return (
    <div className="space-y-4" data-testid="her-messages-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>HER Messages</h1>
      </div>

      <div className="grid md:grid-cols-[280px_1fr] gap-4 h-[calc(100vh-12rem)]">
        <Card className="border-0 shadow-sm rounded-2xl overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-3 space-y-1">
              {contacts.length > 0 ? contacts.map(c => (
                <button
                  key={c.id}
                  onClick={() => setActiveContact(c)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors ${activeContact?.id === c.id ? 'bg-orange-50 border border-orange-200' : 'hover:bg-muted/50'}`}
                  data-testid={`her-contact-${c.id}`}
                >
                  <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center text-sm font-semibold text-orange-700">
                    {c.full_name?.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{c.full_name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{c.role?.replace('_', ' ')}</p>
                  </div>
                </button>
              )) : (
                <div className="p-6 text-center"><MessageSquare className="w-8 h-8 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">No contacts yet</p></div>
              )}
            </div>
          </ScrollArea>
        </Card>

        <Card className="border-0 shadow-sm rounded-2xl flex flex-col overflow-hidden">
          {activeContact ? (
            <>
              <div className="px-4 py-3 border-b bg-white/80">
                <p className="font-medium text-sm">{activeContact.full_name}</p>
                <p className="text-xs text-muted-foreground capitalize">{activeContact.role?.replace('_', ' ')}</p>
              </div>
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-3">
                  {messages.map(m => (
                    <div key={m.id} className={`flex ${m.sender_id === user.id ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] px-3.5 py-2.5 rounded-2xl text-sm ${m.sender_id === user.id ? 'bg-orange-600 text-white rounded-br-sm' : 'bg-muted rounded-bl-sm'}`}>
                        <p>{m.content}</p>
                        <p className={`text-[10px] mt-1 ${m.sender_id === user.id ? 'text-orange-100' : 'text-muted-foreground'}`}>{timeAgo(m.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>
              </ScrollArea>
              <div className="p-3 border-t bg-white space-y-2">
                <DictationInput
                  value={newMsg}
                  onChange={(cleaned) => setNewMsg(cleaned)}
                  rows={2}
                  placeholder="Type or speak a message..."
                  promptContext={{
                    context: 'her_message',
                    taskName: 'HER message',
                    taskDescription: `Message to ${activeContact.full_name}`,
                    category: 'heat'
                  }}
                  data-testid="her-message-input"
                />
                <Button onClick={handleSend} className="bg-orange-600 hover:bg-orange-700 text-white rounded-full w-full" data-testid="her-send-message">
                  Send Message
                </Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center"><MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">Select a contact to start messaging</p></div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
